import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';

function WholeLinePlaceholder({
  textClasses,
  textHeightPixels,
}: {
  textClasses: string;
  textHeightPixels: number;
}) {
  return (
    <div
      className={mergeStyles(textClasses, 'rounded bg-grey-40')}
      style={{ height: `${textHeightPixels}px` }}
    ></div>
  );
}

function SplitLinePlaceholder({
  lineSplit,
  textClasses,
  textHeightPixels,
}: {
  lineSplit: number;
  textClasses: string;
  textHeightPixels: number;
}) {
  if (lineSplit === 0) {
    return (
      <WholeLinePlaceholder
        textClasses={textClasses}
        textHeightPixels={textHeightPixels}
      />
    );
  }

  /**
   * Because tailwind needs all the classes to be defined in the codebase to generate the CSS, we can't dynamically generate the grid column spans for the split line placeholder, so instead we generate a random number to determine where the split goes and then use that to determine which of the predefined grid layouts to render.
   * col-span-1 col-span-2 col-span-3 col-span-4 col-span-5 col-span-6 col-span-7
   */

  return (
    <div className="grid grid-cols-8 gap-4">
      <div
        className={mergeStyles(
          textClasses,
          `col-span-${lineSplit} rounded bg-grey-40`
        )}
        style={{ height: `${textHeightPixels}px` }}
      ></div>
      <div
        className={mergeStyles(
          textClasses,
          `col-span-${8 - lineSplit} rounded bg-grey-40`
        )}
        style={{ height: `${textHeightPixels}px` }}
      ></div>
    </div>
  );
}

function PlaceholderLines({
  lines,
  textHeightPixels,
  textClasses,
}: {
  lines: number;
  textHeightPixels: number;
  textClasses: string;
}) {
  if (lines === 1) {
    return (
      <WholeLinePlaceholder
        textClasses={textClasses}
        textHeightPixels={textHeightPixels}
      />
    );
  }

  const splits = [] as Array<number>;
  for (let i = 0; i < lines; i++) {
    // Deterministic split pattern prevents SSR/client hydration mismatches.
    let lineSplit = (i * 5 + lines * 3) % 8;

    // Don't allow the same split twice in a row for better visual variety
    if (splits[i - 1] === lineSplit) {
      lineSplit = (lineSplit + 1) % 8;
    }

    splits.push(lineSplit);
  }

  return splits.map((split, index) => (
    <SplitLinePlaceholder
      key={index}
      lineSplit={split}
      textClasses={textClasses}
      textHeightPixels={textHeightPixels}
    />
  ));
}

export function SkeletonText({
  heightPixels = 320,
  textHeightPixels = 8,
  textClasses = '',
}: {
  heightPixels?: number;
  textHeightPixels?: number;
  textClasses?: string;
}) {
  // Each line is textHeight "text" + 8px gap, so total per line
  const lines = Math.floor(heightPixels / (textHeightPixels + 8)) || 1;

  return (
    <div
      className="flex animate-pulse items-center"
      style={{ minHeight: `${heightPixels}px` }}
    >
      <div className="flex-1 space-y-2">
        <PlaceholderLines
          lines={lines}
          textHeightPixels={textHeightPixels}
          textClasses={textClasses}
        />
      </div>
    </div>
  );
}

export function SkeletonImage({
  aspectRatio = 'aspect-square',
  widthPixels,
}: {
  aspectRatio?: string;
  widthPixels?: number;
}) {
  return (
    <div
      className={`${aspectRatio}`}
      style={{ width: widthPixels ? `${widthPixels}px` : '100%' }}
    >
      <div className="animate-pulse bg-grey-40 h-full w-full"></div>
    </div>
  );
}
